import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppPageLayout } from './components/PageLayout';
import { useHardMatches } from '../../hooks/useHardMatches';
// Dialog removed in favor of dedicated page
import HardMatch from '../../models/HardMatch';
import { useAuth } from '../../modules/authentication/AuthProvider';
import { useUserProfile } from '../../hooks/useUserProfile';
import { Card, Button, Badge } from 'react-daisyui';
import routes from '../../modules/routes/Routes';
import { Link, useNavigate } from 'react-router-dom';
import MatchCard from './components/MatchCard';
import { shouldShowProcessingState } from './matchingPageLogic';

const TelegramNotificationPrompt: React.FC = () => {
    const { t } = useTranslation();
    return (
        <Card className="bg-base-200 shadow-lg mb-8">
            <Card.Body>
                <div className="flex flex-col md:flex-row items-center justify-between">
                    <div className="md:w-1/2 mb-4 md:mb-0">
                        <h3 className="text-2xl font-bold mb-4">{t('pages.matchingPage.telegramNotificationPrompt.title')}</h3>
                        <p className="mb-4">{t('pages.matchingPage.telegramNotificationPrompt.description')}</p>
                        <ul className="list-disc list-inside mb-4">
                            {t('pages.matchingPage.telegramNotificationPrompt.advantages', { returnObjects: true }).map((advantage, index) => (
                                <li key={index}>{advantage}</li>
                            ))}
                        </ul>
                        <Link to={routes.app.profile.telegramRegistration.build()}>
                            <Button color="primary" data-umami-event="Matching Telegram-Notification-Setup">
                                {t('pages.matchingPage.telegramNotificationPrompt.setupButton')}
                            </Button>
                        </Link>
                    </div>
                    <div className="md:w-1/2 flex justify-center">
                        <img 
                            src="/app/mobile-telegram.png" 
                            alt="Telegram Notifications" 
                            className="max-w-full h-auto max-h-64"
                        />
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

const MatchesProcessingProgress: React.FC = () => {
    const { hardMatchesInProcessing } = useHardMatches();
    const { t } = useTranslation();

    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState('Searching through Immowelt');
    const [startTime] = useState(Date.now());

    useEffect(() => {
        let interval: NodeJS.Timeout;

        // Use logarithmic progress that reaches 80% after 60 seconds
        interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 80 && hardMatchesInProcessing === 0) return prev;
                
                // Calculate time elapsed in seconds
                const timeElapsed = (Date.now() - startTime) / 1000;
                
                // Logarithmic formula: progress = a * ln(time + 1)
                // Solving for a when progress = 80 at time = 60:
                // 80 = a * ln(61)
                // a = 80 / ln(61) ≈ 19.64
                const a = 19.64;
                const newProgress = a * Math.log(timeElapsed + 1);
                
                // Update message based on progress
                const progressBasedMessage = (() => {
                    if (newProgress < 20) {
                        return 'Searching through Immowelt';
                    } else if (newProgress < 35) {
                        return 'Searching through Wohnverdient';
                    } else if (newProgress < 50) {
                        return 'Searching through Kleinanzeigen';
                    } else if (newProgress < 65) {
                        return 'Analyzing the listings for quality and completeness';
                    } else if (newProgress < 75) {
                        return 'Extracting key details from each listing';
                    } else if (newProgress < 80) {
                        return 'Comparing listings against your search criteria';
                    } else {
                        return 'Almost done! Just a few more moments...';
                    }
                })();
                setMessage(progressBasedMessage);
                
                return Math.min(hardMatchesInProcessing === 0 ? 80 : 100, newProgress);
            });
        }, 250);

        return () => {
            clearInterval(interval);
        };
    }, [hardMatchesInProcessing, startTime]);

    return (
        <div className="flex flex-col items-center justify-center h-full mb-28">
            <div className="text-center mb-4">
                <h2 className="text-2xl font-bold mb-4">{t('pages.matchingPage.searchingForMatches')}</h2>
                {/* <p>{t('pages.matchingPage.searchingDescription')}</p> */}
                {/* <p className="mt-4">{message}</p> */}
                {hardMatchesInProcessing > 0 ? (
                    <p>{t('pages.matchingPage.processingMatches', { count: hardMatchesInProcessing })}</p>
                ) : (
                    <p>{message}</p>
                )}
            </div>
            <div className="w-64 h-4 bg-base-200 rounded-full overflow-hidden">
                <div 
                    className="h-full bg-primary transition-all duration-500 ease-out rounded-full"
                    style={{ width: `${progress}%` }}
                />
            </div>
            <div className="mt-2">{Math.round(progress)}%</div>
        </div>
    )
}

const MatchingPage: React.FC = () => {
    const { t } = useTranslation();
    const { userProfile } = useUserProfile();
    const navigate = useNavigate();
    const { isLoading, hardMatches, hardMatchesInProcessing } = useHardMatches();
    // Dialog state removed
    const { telegramRegistered } = useUserProfile();
    // Always show all matches
    const filteredMatches = hardMatches;

    // Separate matches based on close_match_properties
    const exactMatches = filteredMatches.filter(match => !match.close_match_properties);
    const closeMatches = filteredMatches.filter(match => match.close_match_properties);

    // Sort matches by score in descending order
    const sortMatches = (matches: typeof filteredMatches) => 
        [...matches].sort((a, b) => new Date(b.flat.created_at).getTime() - new Date(a.flat.created_at).getTime());

    const sortedExactMatches: HardMatch[] = sortMatches(exactMatches);
    const sortedCloseMatches: HardMatch[] = sortMatches(closeMatches);

    // In initial search there can be zero matches while the pipeline is still running.
    // As soon as any match exists (including hidden/applied), keep the matches view visible.
    const isProcessingHardMatches = shouldShowProcessingState(
        hardMatches.length,
        userProfile?.updated_at,
    );

    return (
        <AppPageLayout currentPage="matches">
            <div className="p-4 h-full">
                {isProcessingHardMatches ? (
                    <>
                        <MatchesProcessingProgress />

                        {!telegramRegistered && <TelegramNotificationPrompt />}
                    </>
                ) : (
                    <>
                        {sortedExactMatches.length > 0 && (
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="h-1 w-1 bg-green-600 rounded-full"></div>
                                    <h2 className="text-xl font-bold text-gray-900">{t('pages.matchingPage.matchesFoundTitle')}</h2>
                                    <span className="text-sm text-gray-500">({sortedExactMatches.length})</span>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
                            {sortedExactMatches.map((match) => (
                                <MatchCard
                                    key={match.id}
                                    match={match}
                                    onClick={() => navigate(routes.app.matchDetails.build(String(match.id)))}
                                />
                            ))}
                        </div>

                        {/*!telegramRegistered && <TelegramNotificationPrompt />*/}

                        {sortedCloseMatches.length > 0 && (
                            <>
                                <div className="mb-4 mt-16">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="h-1 w-1 bg-yellow-600 rounded-full"></div>
                                        <h2 className="text-xl font-bold text-gray-900">{t('pages.matchingPage.closeMatchesTitle')}</h2>
                                        <span className="text-sm text-gray-500">({sortedCloseMatches.length})</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
                                    {sortedCloseMatches.map((match) => (
                                        <MatchCard
                                            key={match.id}
                                            match={match}
                                            onClick={() => navigate(routes.app.matchDetails.build(String(match.id)))}
                                        />
                                    ))}
                                </div>

                                {/*!telegramRegistered && <TelegramNotificationPrompt />*/}
                            </>
                        )}
                        
                        {hardMatchesInProcessing > 0 && (
                            <div className="flex flex-row items-center justify-center md:justify-start mb-8 gap-2">
                                <p>{t('pages.matchingPage.processingMoreMatches', { count: hardMatchesInProcessing })}</p>
                                <div className="loading loading-spinner loading-sm"></div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </AppPageLayout>
    );
};

export default MatchingPage;
